import React, { useEffect, useState } from "react";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import Fade from "@mui/material/Fade";
import {
  Box,
  TextField,
  Typography,
  Button,
  Paper,
  Alert,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import axios from "axios";
import { toast } from "react-toastify";
import { useMediaQuery, useTheme } from "@mui/material";
import WalletIcon from "@mui/icons-material/Wallet";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";

// API Endpoints
const GET_USERS_API = "https://software.tabtrips.com/api/get_user.php";
const UPDATE_USER_API = "https://software.tabtrips.com/api/update_user.php";
const RAZORPAY_CREATE_ORDER_API = "https://software.tabtrips.com/api/create_order.php";
const RAZORPAY_VERIFY_PAYMENT_API = "https://software.tabtrips.com/api/verify_payment.php";
const GET_PROPERTY_HISTORY_API = "https://software.tabtrips.com/api/get_property_history.php";
const GET_TRANSACTION_HISTORY_API = "https://software.tabtrips.com/api/get_transaction_history.php";

export default function UserManagement() {
  const [mode, setMode] = useState(null); // null | "add" | "update"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    hotel_id: "",
    property_name: "",
    property_address: "",
    name: "",
    phone: "",
    email: "",
    joining_date: dayjs().format("YYYY-MM-DD"),
    expiration_date: dayjs().add(1, "month").format("YYYY-MM-DD"),
    payment_status: "Paid",
    total_amount: 0.0,
    reason: "",
    software: "TabTrips",
    role: "",
  });

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedHotelId, setSelectedHotelId] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [propertyHistory, setPropertyHistory] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [historyType, setHistoryType] = useState("property"); // 'property' or 'wallet'
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [addMoneyModalOpen, setAddMoneyModalOpen] = useState(false);
  const [showLogoutTooltip, setShowLogoutTooltip] = useState(false);
  const [razorpayForm, setRazorpayForm] = useState({
    name: "",
    phone: "",
    email: localStorage.getItem("email") || "",
    amount: "",
  });
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0.0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "admin";
  
  // Load Razorpay SDK
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    fetchUsersAndWallet();
  }, []);

  // Fetch users + wallet balance
  const fetchUsersAndWallet = () => {
    setLoading(true);
    const url = userRole && userRole !== "admin"
      ? `${GET_USERS_API}?role=${encodeURIComponent(userRole)}`
      : GET_USERS_API;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          const sorted = data.data.sort((a, b) =>
            dayjs(a.expiration_date).isAfter(dayjs(b.expiration_date)) ? 1 : -1
          );
          setUsers(sorted);
          setFilteredUsers(sorted);

          if (sorted.length > 0 && sorted[0].walletAmount != null) {
            const amount = parseFloat(sorted[0].walletAmount);
            setWalletAmount(amount);
            localStorage.setItem("walletAmount", amount.toFixed(2));
          }
        } else {
          setError("No users found!");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load users.");
        setLoading(false);
      });
  };

  // Search Filter Logic
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredUsers(
        users.filter((user) =>
          Object.values(user).some(
            (val) => val?.toString().toLowerCase().includes(query)
          )
        )
      );
    }
  }, [searchQuery, users]);

  // Export to Excel
  const exportToExcel = () => {
    import("xlsx").then((XLSX) => {
      const worksheetData = users.map((user) => ({
        "Hotel ID": user.hotel_id,
        "Property Name": user.property_name,
        Address: user.property_address,
        Phone: user.phone,
        Email: user.email,
        Joining: dayjs(user.joining_date).format("DD MMM YYYY"),
        // Expiration: dayjs(user.expiration_date).format("DD MMM YYYY"),
        Status: user.payment_status,
        // Reason: user.reason || "N/A",
        // Software: user.software || "N/A",
      }));
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
      XLSX.writeFile(workbook, "User_Management.xlsx");
    });
  };

  // Handle Add User
  const handleAddUser = () => {
    setFormData({
      hotel_id: "",
      property_name: "",
      property_address: "",
      name: "",
      phone: "",
      email: "",
      joining_date: dayjs().format("YYYY-MM-DD"),
      expiration_date: dayjs()
        .add(selectedMonths, "month")
        .format("YYYY-MM-DD"),
      payment_status: "Paid",
      total_amount: 0.0,
      reason: "",
      software: "TabTrips",
      role: "",
    });
    setMode("add");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "expiration_date" && dayjs(value).isAfter(dayjs())) {
      setFormData((prev) => ({ ...prev, payment_status: "Paid" }));
    }
  };

  const handleMonthChange = (e) => {
    const monthsToAdd = Number(e.target.value);
    setSelectedMonths(monthsToAdd);
    setFormData((prev) => ({
      ...prev,
      expiration_date: dayjs()
        .add(monthsToAdd, "month")
        .format("YYYY-MM-DD"),
    }));
  };

  // Submit Form
  const handleSubmit = () => {
    if (formData.payment_status === "Pending" && !formData.reason.trim()) {
      formData.reason = "Payment pending";
    }

    setLoading(true);
    fetch(UPDATE_USER_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          toast.success(mode === "add" ? "User added!" : "User updated!");
          fetchUsersAndWallet();
          setMode(null);
        } else {
          toast.error("Failed to submit user: " + data.message);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error submitting user");
        setLoading(false);
      });
  };

  // Edit User
  const handleEditUser = (hotel_id) => {
    setLoading(true);
    fetch(`${GET_USERS_API}?search=${hotel_id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.data.length > 0) {
          const userData = data.data[0];
          if (!userData.reason) userData.reason = "";
          if (!userData.software) userData.software = "TabTrips";
          setFormData(userData);
          setMode("update");
          setError(null);
        } else {
          setError("User not found");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch user data");
        setLoading(false);
      });
  };

  // Row Click – Show Property Details
  const handleRowClick = (row) => {
    setSelectedUser(row);
    setDetailsDialogOpen(true);
  };

  // Open Details Dialog
  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedUser(null);
  };

  // Fetch Property History by hotel_id
  const openPropertyHistory = async (hotel_id) => {
    setIsFetchingHistory(true);
    try {
      const res = await axios.get(`${GET_PROPERTY_HISTORY_API}?hotel_id=${hotel_id}`);
      if (res.data.status === "success") {
        setPropertyHistory(res.data.data);
        setHistoryType("property");
        setHistoryModalOpen(true);
      } else {
        toast.warn("No property history found.");
      }
    } catch (err) {
      toast.error("Failed to fetch property history.");
    } finally {
      setIsFetchingHistory(false);
    }
  };

  // Fetch Wallet Transactions
  const fetchTransactionHistory = async () => {
    setIsFetchingHistory(true);
    try {
      const userEmail = razorpayForm.email;
      const res = await axios.get(`${GET_TRANSACTION_HISTORY_API}?email=${userEmail}`);
      if (res.data.status === "success") {
        setTransactionHistory(res.data.data);
        
        setHistoryType("wallet");
        setHistoryModalOpen(true);
      } else {
        toast.warn("No transactions found in wallet.");
      }
    } catch (err) {
      toast.error("Failed to fetch wallet history.");
    } finally {
      setIsFetchingHistory(false);
    }
  };

  // Handle Add Money Modal Changes
  const handleRazorpayChange = (e) => {
    const { name, value } = e.target;
    setRazorpayForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitRazorpay = async () => {
    const { name, phone, email, amount } = razorpayForm;
    if (
      !name.trim() ||
      !phone.trim() ||
      !amount ||
      isNaN(amount) ||
      amount <= 0
    ) {
      toast.warn("Please fill all fields correctly.");
      return;
    }

    try {
      const res = await axios.post(RAZORPAY_CREATE_ORDER_API, {
        amount: parseFloat(amount),
        email,
        name,
        phone,
      });

      openRazorpayCheckout(res.data.orderId, res.data.amountInPaise);
    } catch (err) {
      toast.error("Failed to create order. Please try again.");
    }

    setAddMoneyModalOpen(false);
    setRazorpayForm({ name: "", phone: "", email: localStorage.getItem("email") || "", amount: "" });
  };

  const openRazorpayCheckout = (orderId, amountInPaise) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: amountInPaise.toString(),
      currency: "INR",
      name: "TabTrips Wallet",
      description: "Top-up your wallet",
      order_id: orderId,
      handler: function (response) {
        axios
          .post(RAZORPAY_VERIFY_PAYMENT_API, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            email: razorpayForm.email,
            amount: razorpayForm.amount,
            name: razorpayForm.name,
            phone: razorpayForm.phone,
          })
          .then((res) => {
            if (res.data.status === "success") {
              const newBalance = parseFloat(res.data.newBalance);
              if (!isNaN(newBalance)) {
                setWalletAmount(newBalance);
                localStorage.setItem("walletAmount", newBalance.toFixed(2));
                toast.success("Payment verified and wallet updated!");
                setTimeout(() => window.location.reload(), 3000);
              } else {
                toast.error("Invalid balance received");
              }
            } else {
              toast.error("Verification failed: " + res.data.message);
            }
          })
          .catch((err) => {
            console.error("Verification failed:", err);
            toast.error("Payment verification failed.");
          });
        setAddMoneyModalOpen(false);
      },
      prefill: {
        name: razorpayForm.name,
        email: razorpayForm.email,
        contact: razorpayForm.phone,
      },
      theme: {
        color: "#673ab7",
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // Deactivation Modal
  const openDeactivateModal = (hotel_id) => {
    setSelectedHotelId(hotel_id);
    setDeactivationReason("");
    setDeactivateModalOpen(true);
  };

  const handleDeactivateSubmit = () => {
    if (!deactivationReason.trim()) {
      toast.warn("Reason is required for deactivation.");
      return;
    }

    const deactivatedUser = users.find((user) => user.hotel_id === selectedHotelId);
    if (!deactivatedUser) {
      toast.warn("User not found");
      return;
    }

    const dataForUpdate = {
      ...deactivatedUser,
      expiration_date: dayjs().format("YYYY-MM-DD"),
      payment_status: "Pending",
      reason: deactivationReason,
    };

    fetch(UPDATE_USER_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dataForUpdate),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.status === "success") {
          setUsers((prev) =>
            prev.map((user) =>
              user.hotel_id === selectedHotelId ? { ...user, ...dataForUpdate } : user
            )
          );
          toast.success("Membership deactivated successfully.");
        } else {
          toast.error("Failed to deactivate membership: " + result.message);
        }
        setLoading(false);
        setDeactivateModalOpen(false);
      })
      .catch(() => {
        toast.error("Error deactivating membership");
        setLoading(false);
        setDeactivateModalOpen(false);
      });
  };

  // Wallet Actions
  const openAddMoneyDialog = () => {
    setRazorpayForm((prev) => ({
      ...prev,
      email: localStorage.getItem("email") || "",
    }));
    setAddMoneyModalOpen(true);
  };

  const closeAddMoneyDialog = () => {
    setAddMoneyModalOpen(false);
    setRazorpayForm({ name: "", phone: "", email: "", amount: "" });
  };

  // Close History Dialog
  const closeHistoryDialog = () => {
    setHistoryModalOpen(false);
    setPropertyHistory([]);
    setTransactionHistory([]);
  };

  // Render history dialog content
  const renderHistoryDialogContent = () => {
    if (isFetchingHistory) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[...Array(5)].map((_, i) => (
            <Box key={i} sx={{ p: 1.5, mb: 1, bgcolor: "#f0f0f0", borderRadius: "8px" }}>
              <Typography sx={{ fontWeight: 500 }}>Loading...</Typography>
              <Typography variant="caption">₹0.00</Typography>
            </Box>
          ))}
        </Box>
      );
    }
  
    if (historyType === "property" && propertyHistory.length > 0) {
      return (
        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {propertyHistory.map((record, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                bgcolor: record.status === "Deactivated"
                  ? "rgba(211, 47, 47, 0.1)"
                  : "rgba(46, 125, 50, 0.1)",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 500 }}>{record.status}</Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {dayjs(record.changed_at).format("DD MMM YYYY HH:mm:ss")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: record.status === "Deactivated" ? "red" : "green",
                }}
              >
                {record.status === "Deactivated" ? "-" : "+"} ₹{parseFloat(record.amount || 0).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    } else if (historyType === "wallet" && transactionHistory.length > 0) {
      // Sort transactions by date (oldest first)
      const sortedTransactions = [...transactionHistory].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
  
      return (
        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {sortedTransactions.map((tx, index) => (
            <Box
              key={tx.id || `tx-${index}`} // Unique fallback key
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                bgcolor:
                  tx.type === "credit"
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(211, 47, 47, 0.1)",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 500 }}>
                  {tx.type === "debit" ? tx.hotel_name : tx.id || "System"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {dayjs(tx.date).format("DD MMM YYYY")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: tx.type === "credit" ? "green" : "red",
                }}
              >
                {tx.type === "credit" ? "+" : "-"} ₹{Math.abs(tx.amount).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
  
    // Default fallback if no history matches
    return <Typography>No history found.</Typography>;
  };
  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 4 },
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Header with Wallet */}
      {/* Header with Wallet & Logout Icon */}
<Box
  sx={{
    textAlign: "center",
    py: { xs: 2.5, sm: 3 },
    px: 2,
    backgroundColor: "#8e9ba6",
    color: "#fff",
    mb: 3,
    minHeight: { xs: "70px", sm: "80px" },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  }}
>
  {/* Logout Icon - Top Left Corner with Tooltip */}
  <Box
    sx={{
      position: "absolute",
      top: { xs: 12, sm: 16 },
      left: { xs: 16, sm: 32 },
      zIndex: 12,
    }}
    onMouseEnter={() => setShowLogoutTooltip(true)}
    onMouseLeave={() => setShowLogoutTooltip(false)}
  >
    <IconButton
      onClick={() => {
        // Clear specific items from localStorage
        localStorage.removeItem("walletAmount");
        localStorage.removeItem("walletHash");
        localStorage.removeItem("userRole");
        localStorage.removeItem("authToken");

        window.location.reload();
      }}
      sx={{
        color: "#fff",
        bgcolor: "rgba(255,255,255,0.2)",
        "&:hover": {
          bgcolor: "rgba(255,255,255,0.3)",
        },
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        transition: "all 0.2s ease-in-out",
      }}
      aria-label="Logout"
    >
      <PowerSettingsNewIcon />
    </IconButton>

    {/* Tooltip */}
    {showLogoutTooltip && (
      <Box
        sx={{
          position: "absolute",
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          mt: 0.5,
          bgcolor: "#333",
          color: "#fff",
          padding: "4px 10px",
          borderRadius: "4px",
          whiteSpace: "nowrap",
          fontSize: "0.75rem",
          fontWeight: 500,
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          zIndex: 13,
        }}
      >
        Logout
      </Box>
    )}
  </Box>

  {/* State for Tooltip Visibility */}
  {useEffect(() => {}, [])}

  {/* Wallet Box Inside Header - Bottom Right Side */}
  <Box
    sx={{
      position: "absolute",
      bottom: { xs: 12, sm: 16 },
      right: { xs: 16, sm: 32 },
      zIndex: 10,
    }}
  >
    {!isMobile ? (
      <Box
        sx={{
          backgroundColor: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          borderRadius: "10px",
          width: "280px",
          textAlign: "center",
          py: 1.5,
          px: 2,
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          },
          border: "1px solid #eee",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                color: walletAmount >= 0 ? "#2e7d32" : "#d32f2f",
                lineHeight: 1.2,
              }}
            >
              ₹{walletAmount?.toFixed(2)}
            </Typography>
          </Box>
          <IconButton onClick={fetchTransactionHistory}>
            <HistoryIcon sx={{ color: "#673ab7" }} />
          </IconButton>
        </Box>

        <Button
          onClick={openAddMoneyDialog}
          size="small"
          fullWidth
          startIcon={<Typography>₹</Typography>}
          sx={{
            mt: 1,
            bgcolor: "#673ab7",
            color: "#fff",
            textTransform: "none",
            fontSize: "0.8rem",
            borderRadius: "6px",
            "&:hover": { bgcolor: "#512da8" },
          }}
        >
          Add Money
        </Button>
      </Box>
    ) : (
      <IconButton
        onClick={() => setIsWalletOpen((prev) => !prev)}
        sx={{
          bgcolor: "#fff",
          color: "#673ab7",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          "&:hover": { bgcolor: "#f5f5f5" },
        }}
      >
        <WalletIcon />
      </IconButton>
    )}

    {/* Mobile Wallet Box */}
    {isMobile && isWalletOpen && (
      <Box
        sx={{
          position: "absolute",
          top: "100%",
          right: 0,
          mt: 1,
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          borderRadius: "10px",
          width: "220px",
          textAlign: "center",
          py: 2,
          px: 2.5,
          zIndex: 11,
          border: "1px solid #eee",
        }}
      >
        <IconButton
          onClick={() => setIsWalletOpen(false)}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="body2"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
            gap: 0.5,
            fontWeight: 600,
            fontSize: "1rem",
            color: walletAmount >= 0 ? "#2e7d32" : "#d32f2f",
          }}
        >
          ₹{walletAmount?.toFixed(2)}
        </Typography>
        <Button
          onClick={openAddMoneyDialog}
          size="small"
          fullWidth
          startIcon={<Typography>₹</Typography>}
          sx={{
            mt: 1,
            bgcolor: "#673ab7",
            color: "#fff",
            textTransform: "none",
            fontSize: "0.75rem",
            borderRadius: "6px",
            "&:hover": { bgcolor: "#512da8" },
          }}
        >
          Add Money
        </Button>
      </Box>
    )}
  </Box>

  {/* Header Title - Centered */}
  <Fade in timeout={800}>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        letterSpacing: "1px",
        fontFamily: "'Poppins', sans-serif",
        textTransform: "uppercase",
        position: "relative",
        zIndex: 1,
        color: "#fff",
      }}
    >
      User Management
    </Typography>
  </Fade>
</Box>
      {/* Search Bar */}
      {/* Search Bar */}
<Box sx={{ maxWidth: "600px", margin: "0 auto 30px auto", width: "100%", px: 2 }}>
  <TextField
    label="Search Users..."
    variant="outlined"
    fullWidth
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    sx={{
      "& .MuiInputBase-root": {
        transition: "box-shadow 0.3s ease",
        "&:focus-within": {
          boxShadow: "0 0 8px rgba(100, 100, 255, 0.5)",
        },
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#fff",
      },
    }}
    InputProps={{
      style: { borderRadius: "8px" },
    }}
  />
</Box>

{/* Admin Filter Buttons - Only show if user is admin */}
{isAdmin ? (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 3,
      mb: 3,
      px: 2,
    }}
  >
    {/* Show Admins Button */}
    <Button
      variant="contained"
      onClick={() => setFilteredUsers(users.filter((u) => u.role === "manager"))}
      sx={{
        bgcolor: "#1976d2",
        color: "#fff",
        fontSize: "1rem",
        fontWeight: 600,
        padding: "12px 24px",
        borderRadius: "12px",
        boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        minWidth: "180px",
        textTransform: "none",
        "&:hover": {
          bgcolor: "#1565c0",
        },
      }}
    >
      OTA Guru
    </Button>

    {/* Show OJO Button */}
    <Button
      variant="contained"
      onClick={() => setFilteredUsers(users.filter((u) => u.role === "ojo"))}
      sx={{
        bgcolor: "#f57c00",
        color: "#fff",
        fontSize: "1rem",
        fontWeight: 600,
        padding: "12px 24px",
        borderRadius: "12px",
        boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        minWidth: "180px",
        textTransform: "none",
        "&:hover": {
          bgcolor: "#ef6c00",
        },
      }}
    >
      OJO
    </Button>

    {/* Show All Button */}
    <Button
      variant="outlined"
      onClick={() => setFilteredUsers(users)}
      sx={{
        borderColor: "#9e9e9e",
        color: "#333",
        fontSize: "1rem",
        fontWeight: 600,
        padding: "12px 24px",
        borderRadius: "12px",
        minWidth: "180px",
        textTransform: "none",
        "&:hover": {
          borderColor: "#757575",
          backgroundColor: "#fafafa",
        },
      }}
    >
      Show All
    </Button>
  </Box>
) : (
  <Box sx={{ textAlign: "center", my: 3 }}>
    <Typography variant="h5" color="primary">
      Welcome
    </Typography>
  </Box>
)}
      {/* Export & Back Button */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button
          variant="contained"
          onClick={exportToExcel}
          sx={{
            backgroundColor: "#673ab7",
            "&:hover": { backgroundColor: "#512da8" },
          }}
        >
          Export to Excel
        </Button>
        {mode && (
          <Button onClick={() => setMode(null)} variant="outlined">
            Back to Main Page
          </Button>
        )}
      </Box>

      {/* Error Message */}
      {error && <Alert severity="error">{error}</Alert>}

      {/* Table View */}
      {!mode && (
        <>
          {isAdmin && (
            <Button
              variant="contained"
              onClick={handleAddUser}
              sx={{
                backgroundColor: "#673ab7",
                "&:hover": { backgroundColor: "#512da8" },
                mb: 2,
              }}
            >
              Add New User
            </Button>
          )}
          <Paper elevation={3} sx={{ padding: 2, borderRadius: "15px" }}>
            <DataGrid
              rows={filteredUsers}
              columns={[
                { field: "hotel_id", headerName: "Hotel ID", width: 100 },
                {
                  field: "property_name",
                  headerName: "Property Name",
                  flex: 1,
                  minWidth: 150,
                },
                {
                  field: "property_address",
                  headerName: "Address",
                  flex: 1,
                  minWidth: 150,
                },
                { field: "phone", headerName: "Phone", width: 130 },
                { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
                {
                  field: "joining_date",
                  headerName: "Joining Date",
                  renderCell: (params) =>
                    dayjs(params.value).format("DD MMM YYYY"),
                  width: 130,
                },
                {
                  field: "payment_status",
                  headerName: "Status",
                  width: 110,
                  renderCell: (params) =>
                    params.value === "Paid" ? (
                      <span style={{ color: "green" }}>✅ Paid</span>
                    ) : (
                      <span style={{ color: "red" }}>❌ Pending</span>
                    ),
                },
              ]}
              getRowId={(row) => row.hotel_id}
              disableSelectionOnClick
              onRowClick={(params) => handleRowClick(params.row)}
              pageSize={5}
              rowsPerPageOptions={[5]}
              loading={loading}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f0f2f5",
                  fontWeight: "bold",
                  color: "#333",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ddd",
                },
              }}
            />
          </Paper>
        </>
      )}

      {/* Details Dialog
      <Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedUser?.property_name || "User Details"}
          <IconButton onClick={closeDetailsDialog} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUser ? (
            <>
              <Typography><strong>Hotel ID:</strong> {selectedUser.hotel_id}</Typography>
              <Typography><strong>Address:</strong> {selectedUser.property_address}</Typography>
              <Typography><strong>Phone:</strong> {selectedUser.phone}</Typography>
              <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
              <Typography><strong>Joining Date:</strong> {dayjs(selectedUser.joining_date).format("DD MMM YYYY")}</Typography>
              <Typography><strong>Status:</strong> {selectedUser.payment_status}</Typography>
              <Typography><strong>Total Amount:</strong> {selectedUser.total_amount}</Typography>
              <Typography><strong>Reason:</strong> {selectedUser.reason || "N/A"}</Typography>
              <Typography><strong>Software:</strong> {selectedUser.software || "N/A"}</Typography>
            </>
          ) : (
            <Typography>Loading user details...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {isAdmin && (
            <Button onClick={() => handleEditUser(selectedUser?.hotel_id)} variant="contained" color="primary">
              Edit
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => openDeactivateModal(selectedUser?.hotel_id)} variant="contained" color="error">
              Deactivate
            </Button>
          )}
          <Button onClick={() => openPropertyHistory(selectedUser?.hotel_id)} variant="contained" color="info">
            History
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* Transaction History Dialog */}
      <Dialog open={historyModalOpen} onClose={closeHistoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {historyType === "property" ? "Property Activity History" : "Wallet Transaction History"}
          <IconButton onClick={closeHistoryDialog} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>{renderHistoryDialogContent()}</DialogContent>
        <DialogActions>
          <Button onClick={closeHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Deactivation Modal */}
      <Dialog open={deactivateModalOpen} onClose={() => setDeactivateModalOpen(false)}>
        <DialogTitle>Deactivate Membership</DialogTitle>
        <IconButton onClick={() => setDeactivateModalOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <TextField
            label="Reason for Deactivation"
            value={deactivationReason}
            onChange={(e) => setDeactivationReason(e.target.value)}
            fullWidth
            error={!deactivationReason.trim()}
            helperText={!deactivationReason.trim() ? "Reason is required" : ""}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateModalOpen(false)}>Cancel</Button>
          <Button onClick={handleDeactivateSubmit} color="error">
            Confirm Deactivation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Money Modal */}
      <Dialog open={addMoneyModalOpen} onClose={closeAddMoneyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Money to Wallet
          <IconButton onClick={closeAddMoneyDialog} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
            <TextField
              label="Full Name"
              name="name"
              value={razorpayForm.name}
              onChange={handleRazorpayChange}
              fullWidth
              required
              InputProps={{
                sx: { borderRadius: "12px" },
              }}
            />

            <TextField
              label="Phone Number"
              name="phone"
              type="tel"
              value={razorpayForm.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 10) {
                  setRazorpayForm((prev) => ({ ...prev, phone: value }));
                }
              }}
              inputProps={{ maxLength: 10, pattern: "[0-9]*", inputMode: "numeric" }}
              fullWidth
              required
              autoComplete="tel"
              InputProps={{
                sx: { borderRadius: "12px" },
              }}
            />

            <TextField
              label="Email Address"
              name="email"
              value={razorpayForm.email}
              disabled
              fullWidth
              InputProps={{
                sx: {
                  borderRadius: "12px",
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                },
              }}
              InputLabelProps={{
                sx: { color: "#aaa" },
              }}
            />

            <TextField
              label="Amount to Add (₹)"
              name="amount"
              type="number"
              value={razorpayForm.amount}
              onChange={handleRazorpayChange}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                sx: { borderRadius: "12px" },
              }}
              InputLabelProps={{
                sx: { color: "#757575" },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            pb: 3,
            gap: 2,
          }}
        >
          <Button
            onClick={closeAddMoneyDialog}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontWeight: 600,
              borderColor: "#ccc",
              color: "#555",
              "&:hover": {
                borderColor: "#999",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRazorpay}
            variant="contained"
            sx={{
              bgcolor: "#673ab7",
              color: "#fff",
              fontWeight: 600,
              boxShadow: "0 4px 10px rgba(103,58,183,0.3)",
              "&:hover": {
                bgcolor: "#512da8",
                boxShadow: "0 6px 14px rgba(103,58,183,0.4)",
              },
            }}
          >
            Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Section */}
     {/* Form Section */}
{(mode === "add" || mode === "update") && (
  <Paper elevation={3} sx={{ padding: 3, mt: 3, borderRadius: "15px" }}>
    <Typography
      variant="h5"
      gutterBottom
      sx={{
        color: "#555",
        fontWeight: "bold",
        textAlign: "center",
        mb: 2,
      }}
    >
      {mode === "add" ? "Add New User" : "Edit User"}
    </Typography>

    <TextField
      label="Hotel ID"
      name="hotel_id"
      value={formData.hotel_id}
      onChange={handleChange}
      fullWidth
      margin="normal"
      disabled={mode === "update"}
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
      label="Property Name"
      name="property_name"
      value={formData.property_name}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
      label="Property Address"
      name="property_address"
      value={formData.property_address}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
      label="Name"
      name="name"
      value={formData.name}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
      label="Phone"
      name="phone"
      value={formData.phone}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
      label="Email"
      name="email"
      value={formData.email}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
  label="Total Amount"
  name="total_amount"
  type="number"
  step="0.01" // Allows decimal entries like 100.99
  value={formData.total_amount}
  onChange={handleChange}
  fullWidth
  margin="normal"
  InputProps={{ 
    style: { backgroundColor: "#fff", borderRadius: "8px" },
    inputProps: { min: 0 } // Optional: Ensures no negative numbers
  }}
/>

    <Select
      value={formData.role}
      onChange={handleChange}
      name="role"
      fullWidth
      required
      sx={{ mb: 2 }}
    >
      <MenuItem value="">Select Role</MenuItem>
      <MenuItem value="admin">Admin</MenuItem>
      <MenuItem value="ojo">OJO</MenuItem>
    </Select>

    <TextField
      label="Joining Date"
      type="date"
      name="joining_date"
      value={formData.joining_date}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputLabelProps={{ shrink: true }}
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <TextField
      label="Expiration Date"
      type="date"
      name="expiration_date"
      value={formData.expiration_date}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputLabelProps={{ shrink: true }}
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={formData.payment_status === "Paid"}
          onChange={() =>
            setFormData((prev) => ({ ...prev, payment_status: "Paid" }))
          }
        />
      }
      label="Paid"
      sx={{ mr: 2 }}
    />
    <FormControlLabel
      control={
        <Checkbox
          checked={formData.payment_status === "Pending"}
          onChange={() =>
            setFormData((prev) => ({ ...prev, payment_status: "Pending" }))
          }
        />
      }
      label="Pending"
    />

    <TextField
      label="Reason"
      name="reason"
      value={formData.reason}
      onChange={handleChange}
      fullWidth
      margin="normal"
      InputProps={{ style: { backgroundColor: "#fff", borderRadius: "8px" } }}
    />

    <Select
      value={formData.software}
      onChange={(e) =>
        setFormData((prev) => ({ ...prev, software: e.target.value }))
      }
      fullWidth
      margin="normal"
      sx={{ backgroundColor: "#fff", borderRadius: "8px" }}
    >
      <MenuItem value="TabTrips">TabTrips</MenuItem>
      <MenuItem value="StayFlexi">StayFlexi</MenuItem>
      <MenuItem value="Both">Both</MenuItem>
    </Select>

    <Button
      variant="contained"
      onClick={handleSubmit}
      fullWidth
      disableElevation
      sx={{
        mt: 2,
        bgcolor: "#673ab7",
        color: "#fff",
        "&:hover": { bgcolor: "#512da8" },
      }}
    >
      {loading ? "Submitting..." : "Submit"}
    </Button>
  </Paper>
)}

{/* Details Dialog */}
<Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} maxWidth="md" fullWidth>
  <DialogTitle>
    {selectedUser?.property_name || "User Details"}
    <IconButton onClick={closeDetailsDialog} sx={{ position: "absolute", right: 8, top: 8 }}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent>
    {selectedUser ? (
      <>
        <Typography><strong>Hotel ID:</strong> {selectedUser.hotel_id}</Typography>
        <Typography><strong>Address:</strong> {selectedUser.property_address}</Typography>
        <Typography><strong>Phone:</strong> {selectedUser.phone}</Typography>
        <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
        <Typography><strong>Joining Date:</strong> {dayjs(selectedUser.joining_date).format("DD MMM YYYY")}</Typography>
        <Typography><strong>Status:</strong> {selectedUser.payment_status}</Typography>
        <Typography><strong>Total Amount:</strong> {selectedUser.total_amount}</Typography>
        {isAdmin && (<Typography><strong>Reason:</strong> {selectedUser.reason || "N/A"}</Typography>)}
        {isAdmin && (<Typography><strong>Software:</strong> {selectedUser.software || "N/A"}</Typography>)}
      </>
    ) : (
      <Typography>Loading user details...</Typography>
    )}
  </DialogContent>
  <DialogActions>
    {isAdmin && (
      <Button onClick={() => handleEditUser(selectedUser?.hotel_id)} variant="contained" color="primary">
        Edit
      </Button>
    )}
    {isAdmin && (
      <Button onClick={() => openDeactivateModal(selectedUser?.hotel_id)} variant="contained" color="error">
        Deactivate
      </Button>
    )}
    {/* <Button onClick={() => openPropertyHistory(selectedUser?.hotel_id)} variant="contained" color="info">
      History
    </Button> */}
  </DialogActions>
</Dialog>

{/* Deactivation Modal */}
<Dialog open={deactivateModalOpen} onClose={() => setDeactivateModalOpen(false)}>
  <DialogTitle>Deactivate Membership</DialogTitle>
  <IconButton onClick={() => setDeactivateModalOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
    <CloseIcon />
  </IconButton>
  <DialogContent>
    <TextField
      label="Reason for Deactivation"
      value={deactivationReason}
      onChange={(e) => setDeactivationReason(e.target.value)}
      fullWidth
      error={!deactivationReason.trim()}
      helperText={!deactivationReason.trim() ? "Reason is required" : ""}
      margin="normal"
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setDeactivateModalOpen(false)}>Cancel</Button>
    <Button onClick={handleDeactivateSubmit} color="error">
      Confirm Deactivation
    </Button>
  </DialogActions>
</Dialog>

{/* Unified History Dialog - Property + Wallet */}
<Dialog open={historyModalOpen} onClose={closeHistoryDialog} maxWidth="sm" fullWidth>
  <DialogTitle>
    Wallet Transaction History
    <IconButton onClick={closeHistoryDialog} sx={{ position: "absolute", right: 8, top: 8 }}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent>
    {isFetchingHistory ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {[...Array(5)].map((_, i) => (
          <Box key={i} sx={{ p: 1.5, mb: 1, bgcolor: "#f0f0f0", borderRadius: "8px" }}>
            <Typography sx={{ fontWeight: 500 }}>Loading...</Typography>
            <Typography variant="caption">₹0.00</Typography>
          </Box>
        ))}
      </Box>
    ) : transactionHistory.length > 0 ? (
      <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
        {/* Sort by newest first */}
        {transactionHistory
          .slice()
          .sort((a, b) => new Date(b.date) - new Date(a.date)) // newest on top
          .map((tx) => (
            <Box
              key={tx.id || tx.hotel_id || Math.random()}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                bgcolor:
                  tx.type === "credit"
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(211, 47, 47, 0.1)",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 500 }}>
                  {tx.type === "debit" ? tx.hotel_name : tx.id || "System"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {dayjs(tx.date).format("DD MMM YYYY")} {/* Only date shown */}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: tx.type === "credit" ? "green" : "red",
                }}
              >
                {tx.type === "credit" ? "+" : "-"} ₹{Math.abs(tx.amount).toFixed(2)}
              </Typography>
            </Box>
          ))}
      </Box>
    ) : (
      <Typography>No wallet transactions found.</Typography>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={closeHistoryDialog}>Close</Button>
  </DialogActions>
</Dialog>

{/* Add Money Modal */}
<Dialog open={addMoneyModalOpen} onClose={closeAddMoneyDialog} maxWidth="sm" fullWidth>
  <DialogTitle>
    Add Money to Wallet
    <IconButton onClick={closeAddMoneyDialog} sx={{ position: "absolute", right: 8, top: 8 }}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      <TextField
        label="Full Name"
        name="name"
        value={razorpayForm.name}
        onChange={handleRazorpayChange}
        fullWidth
        required
        autoComplete="name"
        InputProps={{
          sx: {
            borderRadius: "12px",
            transition: "background-color 0.3s ease",
            "&:hover": { backgroundColor: "#f3f3f3" },
          },
        }}
        InputLabelProps={{
          sx: { color: "#757575" },
        }}
      />

      <TextField
        label="Phone Number"
        name="phone"
        type="tel"
        value={razorpayForm.phone}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, "");
          if (value.length <= 10) {
            handleRazorpayChange({ target: { name: "phone", value } });
          }
        }}
        inputProps={{ maxLength: 10, pattern: "[0-9]*", inputMode: "numeric" }}
        fullWidth
        required
        autoComplete="tel"
        InputProps={{
          sx: {
            borderRadius: "12px",
            transition: "background-color 0.3s ease",
            "&:hover": { backgroundColor: "#f3f3f3" },
          },
        }}
        InputLabelProps={{
          sx: { color: "#757575" },
        }}
      />

      <TextField
        label="Email Address"
        name="email"
        value={razorpayForm.email}
        disabled
        fullWidth
        InputProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "#f5f5f5",
            cursor: "not-allowed",
          },
        }}
        InputLabelProps={{
          sx: { color: "#aaa" },
        }}
      />

      <TextField
        label="Amount to Add (₹)"
        name="amount"
        type="number"
        value={razorpayForm.amount}
        onChange={handleRazorpayChange}
        fullWidth
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">₹</InputAdornment>
          ),
          sx: {
            borderRadius: "12px",
            transition: "background-color 0.3s ease",
            "&:hover": { backgroundColor: "#f3f3f3" },
          },
        }}
        InputLabelProps={{
          sx: { color: "#757575" },
        }}
      />
    </Box>
  </DialogContent>
  <DialogActions
    sx={{
      justifyContent: "center",
      pb: 3,
      gap: 2,
    }}
  >
    <Button
      onClick={closeAddMoneyDialog}
      variant="outlined"
      sx={{
        borderRadius: "8px",
        px: 3,
        py: 1,
        fontWeight: 600,
        borderColor: "#ccc",
        color: "#555",
        "&:hover": {
          borderColor: "#999",
          backgroundColor: "#f5f5f5",
        },
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleSubmitRazorpay}
      variant="contained"
      sx={{
        bgcolor: "#673ab7",
        color: "#fff",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: "0 4px 10px rgba(103,58,183,0.3)",
        "&:hover": {
          bgcolor: "#512da8",
          boxShadow: "0 6px 14px rgba(103,58,183,0.4)",
        },
      }}
    >
      Proceed to Payment
    </Button>
  </DialogActions>
</Dialog>
</Box>
  )};